import * as _ from "lodash";

export const HTTP_BAD_REQUEST = 400;
export const HTTP_UNAUTHORIZED = 401;
export const HTTP_FORBIDDEN = 403;
export const HTTP_NOT_FOUND = 404;
export const HTTP_METHOD_NOT_ALLOWED = 405;
export const HTTP_CONFLICT = 409;

export const CLIENT_TYPE = "student_webapp";

export interface MinMaxPair {
  min: number;
  max: number;
}

export type FlashpackMinMaxCards = {
  [key: number]: MinMaxPair;
};

export interface ClientConfig {
  page_base_url: string;
  api_base_url: string;
  flashpack_min_max_card_constraints: FlashpackMinMaxCards;
}

export interface TeacherAvatarInfo {
  id: number;
  name: string;
  image_url: string;
  preview_image_url: string;
  is_available: boolean;
}

export interface Profile {
  id: number;
  first_name: string;
  first_name_phonetics: string[];
  is_guest: boolean;
  gem_balance: number;
  is_creator: boolean;
  roster_first_name: string;
  roster_last_name: string;
  speaking_rate: number;
  teacher_avatar_info: TeacherAvatarInfo;
  asr_english_accent: number;
  closed_captions_enabled: boolean;
}

export type LessonsInProgress = {
  [key: number]: [number, number | null][];
};

export type CompletedLessons = {
  [key: number]: (number | null)[];
};

export interface ProfileState {
  lessons_in_progress: LessonsInProgress;
  units_state: UnitsState;
  completed_lessons_info: CompletedLessons;
  unlocked_award_uuids: string[];
}

export class PlaybackState {
  sentStartPlaying: boolean = false;
  startPlayingMark: number | null = null;
  bouncingStates: boolean = false;
  showUpsell: boolean = false;
}

export type UnitsState = {
  [unit_id: number]: UnitState;
};

export type UnitState = {
  completed_count: number;
  next_lesson?: { id: number };
};

export const getVersionForLessonInProgress = (
  lessonsInProgress: LessonsInProgress,
  lessonId: number,
  customFlashpackId: number | null
) => {
  return (
    lessonsInProgress[lessonId]?.find((t) => t[1] === customFlashpackId)?.[0] ||
    null
  );
};

export const didProfileCompleteLesson = (
  completedLessons: CompletedLessons,
  lessonId: number,
  customFlashpackId: number | null
) => {
  return completedLessons[lessonId]?.includes(customFlashpackId);
};

export interface User {
  email?: string;
  id: number;
  profiles: Profile[];
  full_access: FullAccess;
  account_type: AccountType;
  is_guest: boolean;
  is_expired: boolean;
  is_staff: boolean;
  is_siwa: boolean;
  has_given_verified_consent: boolean;
  unconfirmed_email_expiration_date?: string | null;
  unconfirmed_email_expiration_in_ms?: string | null;
  unconfirmed_email_for_display?: string;
  has_unconfirmed_email: boolean;
  can_access_school_dashboards: boolean;
  can_access_school_metadata: boolean;
  receives_post_lesson_emails: boolean;
  record_lesson_replays: boolean;
  supports_closed_captions: boolean;
  school_pilot_end_date: string | null;
  school_access_end_date: string | null;
  can_view_teacher_pd: boolean | null;
  flags: { [key: string]: string };
}

export const enum FullAccess {
  TrialMode = 0,
  BetaTester = 1,
  FreePass = 2,
  MonthlySubscription = 3,
  AnnualSubscription = 4,
  SemiAnnualSubscription = 5,
  TrialModePendingPurchase = 6,
  Expired = 7,
  Clever = 8,
  ClassLink = 9,
}

export const enum AccountType {
  Student = 0,
  Teacher = 1,
  Principal = 2,
  DistrictAdmin = 3,
}

export interface Unit {
  id: number;
  title: string;
  slug: string;
  cover_image: string;
  age_min: number | null;
  age_max: number | null;
  short_description: string;
  long_description: string;
  lesson_count: number;
  shared_count: number;
  content_tags: { [key: number]: string[] } | null;
  flashpack_type: FlashpackType | null;
  display_as_lesson: boolean;
  first_lesson: Lesson | null;
  is_game_unit: boolean;
}

export const enum FlashpackType {
  SightWords = 1,
  Spelling = 2,
  Addition = 3,
  Subtraction = 4,
  Multiplication = 5,
  Division = 6,
  Custom = 7,
  Math = 10,
}

export interface FlashpackSubject {
  title: string;
  flashpack_type: FlashpackType;
  is_available: boolean;
  cover_image: string;
}

// For spelling and sight word flashpacks, "item" is a word.
// Some items don't have images associated with them.
export interface FlashpackItem {
  item: string;
  image_url: string | null;
}

export interface LessonRecording {
  id: number;
  video_url: string;
  is_ready: boolean;
  length_seconds: number | null;
  created_date: string;
  recording_type: RecordingType;
  profile_id: number;
  cover_image_url: string;
  lesson_title: string;
}

export const enum RecordingType {
  FocusGroup = 1,
  Replay = 2,
}

export interface MainUnits {
  title: string;
  units: Unit[];
  content_tags: { [key: number]: string[] };
}

export const enum ContentTagType {
  Age = -1,
  Subject = 1,
}

export interface Filter {
  key: number;
  name: string;
  values: string[];
}

export interface FilterInfo {
  all_value: string;
  filters: Filter[];
  section_title_overrides: { [key: string]: string };
}

export interface LessonOrUnitGroup {
  group: LessonOrUnitGroupKind;
  title: string;
  description: string;
  lessons: Lesson[] | null;
  units: Unit[] | null;
  assignment_info: AssignmentInfo | null;
}

export interface LessonAward {
  uuid: string;
  image_url: string;
  name: string;
  description: string;
  is_hidden: boolean;
}

export interface AssignmentInfo {
  lesson_to_highlight: LessonToHighlight | null;
  teacher_id: number;
}

export type LessonToHighlight = [number, number | null];

export const enum LessonOrUnitGroupKind {
  SuggestedLessons = 1,
  WeeklySpecials = 2,
  MainUnits = 4,
  RecentActivity = 6,
  FlashpackUnits = 17,
  GameUnits = 19,
  StudentAssignedLessons = 20,
  // The USF RCT arms serve the same courses group under a different key, but
  // the client renders all three identically (see COURSES_GROUP_KINDS).
  MainUnitsRctControl = 21,
  MainUnitsRctTreatment = 22,
}

export const COURSES_GROUP_KINDS: ReadonlySet<LessonOrUnitGroupKind> = new Set([
  LessonOrUnitGroupKind.MainUnits,
  LessonOrUnitGroupKind.MainUnitsRctControl,
  LessonOrUnitGroupKind.MainUnitsRctTreatment,
]);

export const enum AssessmentType {
  PreAssessment = 1,
  PostAssessment = 2,
}

export interface DownloadableMaterial {
  id: number;
  title: string;
  url: string;
}

export interface LeaderboardEntry {
  rank: number;
  profile_id: number;
  student_name: string;
  gems_earned: number;
}

export interface LeaderboardsResponse {
  this_month_leaderboard: LeaderboardEntry[];
  last_month_leaderboard: LeaderboardEntry[];
  result?: string;
  error_message?: string;
}

export interface UnitProgress {
  id: number;
  title: string;
  started_lesson_count: number;
  completed_lesson_count: number;
  lesson_count: number;
  cover_image: string | null;
  current_weekly_lesson_completion_streak_count: number;
  highest_weekly_lesson_completion_streak_count: number;
}

export interface MilestonesResponse {
  completed_lesson_count: number;
  completed_unit_count: number;
  current_weekly_lesson_completion_streak_count: number;
  highest_weekly_lesson_completion_streak_count: number;
  units_progress: UnitProgress[];
}

export interface NextLessonResponse {
  result: string;
  error_message: string | null;
  current_lesson: Lesson;
  suggested_lessons: Array<Lesson>;
}

export interface Lesson {
  id: number;
  version: number;
  title: string;
  slug: string;
  cover_image: string;
  is_available_in_trial: boolean;
  gems_earned: number;
  short_description: string;
  teacher_short_description: string;
  long_description: string;
  teacher_long_description: string;
  length_minutes: number;
  awards: LessonAward[];
  age_min: number | null;
  age_max: number | null;
  is_flashcard: boolean;
  is_studio_project: boolean;
  custom_flashpack_id: number | null;
  downloadable_materials: DownloadableMaterial[];
  web_content_url: string;
  show_edit_ui: boolean;
  shared: boolean;
  owner_id: number;
  shared_timestamp: number | null;
  owner_first_name: string;
  owner_last_name: string;
  assessment_type: AssessmentType | null;
  universal_link: string;
  is_assigned_by_this_teacher: boolean;
}

export const visibleLessonAwards = (
  awards: LessonAward[],
  unlockedAwardUuids: string[]
) =>
  awards.filter(
    (award) => !award.is_hidden || unlockedAwardUuids.includes(award.uuid)
  );

export function hasAgeRange(
  entity: Pick<Unit, "age_min" | "age_max">
): boolean {
  return entity.age_min !== null || entity.age_max !== null;
}

export interface UnitWithLessons extends Pick<Unit, "age_min" | "age_max"> {
  id: number;
  title: string;
  long_description: string;
  lessons: Lesson[];
  is_studio_unit: boolean;
  cover_image: string | null;
  is_sampler_unit: boolean | null;
  flashpack_type?: FlashpackType;
  supports_flashpack_creation?: boolean;
}

export interface SchoolUser {
  id: number; // user id
  first_name: string;
  last_name: string;
}
export interface SchoolTeacher extends SchoolUser {
  url?: string;
}

export interface SchoolStudent {
  id: number; // profile id
  roster_first_name: string;
  roster_last_name: string;
}

export interface CustomSection {
  id: number;
  name: string;
  created_by: SchoolUser;
  teacher_user_ids?: number[]; // USED FOR CREATION
  student_profile_ids?: number[]; // USED FOR CREATION
  students?: SchoolStudent[]; // used for UI
  teachers?: SchoolTeacher[]; // used for UI
  school: number;
  created_date: Date;
  updated_date: Date;
}

export interface TeacherSectionStudentList {
  id: number;
  name: string;
  student_list: SchoolStudent[];
}

export interface LessonAssignment {
  lesson_id: number;
  custom_flashpack_id: number | null;
}

export interface TeacherLessonAssignments {
  assignments: LessonAssignment[];
  profile_ids?: number[];
  section_id?: number;
  assign_to_all_students?: boolean;
}

export interface ModifyCustomFlashpackRequest {
  title?: string;
  share_with_school?: boolean;
  randomize_card_order?: boolean;
  items?: string[];
}

export interface CustomFlashpack {
  id: number;
  title: string;
  flashpack_type: FlashpackType;
  randomize_card_order: boolean;
  content: { items: FlashpackItem[] };
}

export interface CreateCustomFlashpackResponse {
  flashpack: CustomFlashpack;
  host_lesson_id: number;
  slug: string;
}

export interface ManageSubscriptionUrlResponse {
  result: string;
  url?: string;
  error_message?: string;
}

export interface DeleteAccountResponse {
  result: string;
  error_message?: string;
}

export interface ValidateOfferCodeResponse {
  result: string;
  code: string;
  offer: string;
  user?: User;
  title: string;
  message: string;
  error_message: string;
  enable_store_redemption: boolean;
  google_play_offer_id?: string;
  product_id?: string;
  image_url?: string;
}

export interface StripeSubscriptionProduct {
  product_id: StripeSubscriptionProductId;
  price_usd_cents: number;
  free_trial_days: number;
}

export const enum StripeSubscriptionProductId {
  Monthly = 1,
  Annual = 2,
}

const ANONYMOUS_USER_ID = -1;

export const isUserAnonymous = (user: User) => {
  return user.id === ANONYMOUS_USER_ID;
};

export const isUserSignedUp = (user: User) => {
  return !isUserAnonymous(user) && !user.is_guest;
};

export const isUserSignedUpAndConsented = (user: User) => {
  return isUserSignedUp(user) && user.has_given_verified_consent;
};

export const isSchoolUserTeacher = (user: User) => {
  return isSchoolUser(user) && user.account_type === AccountType.Teacher;
};

export const isNonStudentSchoolUser = (user: User) => {
  return isSchoolUser(user) && user.account_type !== AccountType.Student;
};

export const isSchoolUserStudent = (user: User) => {
  return isSchoolUser(user) && user.account_type === AccountType.Student;
};

export const isUserClever = (user: User) => {
  return user.full_access === FullAccess.Clever;
};

export const isUserClassLink = (user: User) => {
  return user.full_access === FullAccess.ClassLink;
};

export const nonGuestProfiles = (user: User) =>
  user.profiles.filter((profile) => !profile.is_guest);

export const isSchoolUser = (user: User) => {
  return (
    user.full_access === FullAccess.Clever ||
    user.full_access === FullAccess.ClassLink
  );
};

export const isUserExpired = (user: User) => {
  return user.full_access === FullAccess.Expired;
};

export const isOnFreePass = (user: User) => {
  return user.full_access === FullAccess.FreePass;
};

export const isInTrialMode = (user: User) => {
  switch (user.full_access) {
    case FullAccess.TrialMode:
    case FullAccess.TrialModePendingPurchase:
    case FullAccess.Expired:
      return true;
    default:
      return false;
  }
};

export const serializeQueryParamsForDjango = (params, includeNull = false) => {
  const nonarrayParams = _.pickBy(
    params,
    (v) => (includeNull || v) && !Array.isArray(v)
  );
  const arrayParams = _.pickBy(params, (v) => Array.isArray(v));
  const resultParams = new URLSearchParams(nonarrayParams);
  _.forEach(arrayParams, (values, k) =>
    values.forEach((v) => resultParams.append(k, v))
  );
  return resultParams.toString();
};

export interface FetchOptions {
  cache?: RequestCache;
  signal?: AbortSignal;
}

export const enum ButtonType {
  /**
   * These are the static values that determine the kind of button
   * that appears when the lesson is put into waiting mode.
   * They can be found on the websocket messages sent for WebRTC
   * that have the `"type": "wait_time"`. This has a counterpart in
   * the lesson_execution_server
   */
  MICROPHONE = "microphone",
  PLAY = "play",
}

export const enum SpeakingRate {
  SLOW = 100,
  NORMAL = 110,
  FAST = 120,
}

export const nextSpeakingRate = (speakingRate: SpeakingRate) => {
  if (speakingRate === SpeakingRate.SLOW) {
    return SpeakingRate.NORMAL;
  } else if (speakingRate === SpeakingRate.NORMAL) {
    return SpeakingRate.FAST;
  } else {
    return SpeakingRate.SLOW;
  }
};

export interface CoursesResult {
  units: MainUnits;
  featured_units: MainUnits;
  filter_info: FilterInfo;
}

export const enum SearchResultKind {
  Unit = 1,
  Lesson = 2,
}

export interface SearchResultGroup {
  kind: SearchResultKind;
  title: string;
  results: (Unit | Lesson)[];
}

export interface SearchStandardsCode {
  // Postal abbreviation, or "CC" for Common Core (Multi-State).
  state: string;
  code: string;
}

// One deduped explanation of an academic-standards code recognized in the
// query: identical descriptions across states collapse into a single group
// with per-state code chips.
export interface SearchStandardsGroup {
  description: string;
  subject: string;
  grades: string;
  codes: SearchStandardsCode[];
}

export interface SearchResponse {
  results: SearchResultGroup[];
  // Id of the server-side log row for this search — echoed back when
  // submitting search feedback. Null when the search wasn't logged (e.g.
  // blank query); optional to tolerate an older server during deploys.
  id?: number | null;
  // Present only when the query named a known standards code (and the
  // client-side "standards" flag UI wants to render it); optional to
  // tolerate an older server during deploys.
  standards?: SearchStandardsGroup[];
}

export interface LessonStandard {
  jurisdiction: string;
  // Postal abbreviation, or "CC" for Common Core (Multi-State).
  state: string;
  subject: string;
  code: string;
  description: string;
  // Internal AI-tagging quality signal; not rendered to users.
  confidence: "" | "high" | "medium" | "low";
}

export interface LessonStandardsResponse {
  standards: LessonStandard[];
}

// One deduped CC BY 4.0 attribution group from the imported standards data.
// attribution_statement is the publisher's exact credit text and must be
// rendered VERBATIM — reproducing it is the license compliance.
export interface StandardsAttribution {
  attribution_statement: string;
  license: string;
  authors: string[];
  jurisdictions: string[];
  subjects: string[];
  standards_count: number;
}

export interface StandardsAttributionsResponse {
  attributions: StandardsAttribution[];
}

export interface PracticeMetadataResult {
  groups: [LessonOrUnitGroup];
  filter_info: FilterInfo;
}

export interface SchoolInfo {
  id: number;
  name: string;
  district_name: string;
  tutoring_value_dollars: number;
  is_active: boolean;
  last_synced: string;
  l1: number;
  l7: number;
  l28: number;
}

export interface SchoolList {
  schools: [SchoolInfo];
}

export interface SchoolRenewalInfo {
  id: number;
  name: string;
  district_name: string;
  is_active: boolean;
  // "YYYY-MM-DD", or null when the date is not set.
  pilot_end_date: string | null;
  access_end_date: string | null;
  account_manager_email: string | null;
}

export interface SchoolRenewalList {
  schools: SchoolRenewalInfo[];
}

export interface SchoolInsightReportInfo {
  id: number;
  school_id: number;
  school_name: string;
  district_name: string;
  period_id: number;
  period_label: string;
  period_description: string;
  period_start: string;
  period_end: string;
  // Inclusive last day the report actually covers. Trails period_end while the period is running.
  data_through: string;
  is_partial: boolean;
  generated_at: string | null;
  status: string;
  error: string;
  is_frozen: boolean;
  // null for a FAILED report, which has a trace but no document to open.
  html_url: string | null;
}

export interface SchoolInsightReportList {
  reports: SchoolInsightReportInfo[];
}

export enum SchoolGroupType {
  Base = 1,
  NYCBorough = 2,
  NYCDistrict = 3,
}

export const SCHOOL_GROUP_TYPE_LABELS: Record<SchoolGroupType, string> = {
  [SchoolGroupType.Base]: "BASE",
  [SchoolGroupType.NYCBorough]: "NYC Borough",
  [SchoolGroupType.NYCDistrict]: "NYC District",
};

export interface SchoolGroupInfo {
  id: number;
  name: string;
  type: SchoolGroupType;
  district_name: string;
  num_schools: number;
  tutoring_value_dollars: number;
}

export interface SchoolGroupList {
  school_groups: SchoolGroupInfo[];
}

type FlashcardStatistics = {
  text: string;
  learned: boolean;
  correct_answers_counter: number;
  incorrect_answers_counter: number;
};

export type SchoolDashboard =
  | {
      is_at_district_level: true;
      is_at_school_group_level: false;
      is_at_lesson_execution_level: false;
      is_at_lesson_level: false;
      is_at_teacher_level: false;
      is_at_school_level: false;
      is_at_single_user_level: false;
      is_district_link_visible: true;
      district_id: number;
      district_name: string;
      district_link: string;
      student_profile_cnt: number;
      time_spent_hours: number;
      tutoring_value_dollars: number;
      lessons_completed_cnt: number;
      schools: {
        school_name: string;
        school_link: string;
        student_profile_cnt: number;
        time_spent_hours: number;
        tutoring_value_dollars: number;
        lessons_completed_cnt: number;
      }[];
    }
  | {
      is_at_lesson_execution_level: true;
      is_at_school_group_level: false;
      is_at_district_level: false;
      is_at_lesson_level: false;
      is_at_teacher_level: false;
      is_at_school_level: false;
      is_at_single_user_level: false;
      is_district_link_visible: boolean;
      district_id: number | null;
      district_name: string | null;
      district_link: string | null;
      school_id: number;
      school_name: string;
      school_link: string;
      teacher_id: number;
      teacher_link: string;
      teacher_name: string | null;
      student_user_id: number;
      lesson_id: number;
      custom_flashpack_id: number | null;
      lesson_execution_id: string;
      single_user_name: string;
      single_user_link: string;
      is_good: boolean;
      is_bad: boolean;
      reasons: { reason: string; positive: boolean; negative: boolean }[];
      flashcards_statistics: {
        easy: FlashcardStatistics[];
        intermediate: FlashcardStatistics[];
        difficult: FlashcardStatistics[];
      } | null;
      lesson_title: string;
      execution_length: string;
      execution_date_iso: string;
      cover_asset: string;
    }
  | ({
      is_at_lesson_execution_level: false;
      is_at_school_group_level: false;
      is_at_district_level: false;
      is_district_link_visible: boolean;
      secondary_tab_name: string;
      district_id: number | null;
      district_name: string | null;
      district_link: string | null;
      school_id: number;
      school_name: string;
      student_profile_cnt: number;
      lessons_completed_cnt: number;
      time_spent_hours: number;
      tutoring_value_dollars: number;
    } & (
      | {
          is_at_lesson_level: true;
          is_at_teacher_level: false;
          is_at_school_level: false;
          is_at_single_user_level: false;
          school_id: number;
          school_link: string;
          teacher_id: number;
          teacher_link: string;
          teacher_name: string;
          show_only_assigned_students: boolean;
          lesson_id: number;
          custom_flashpack_id: number | null;
          lesson_title: string;
          cover_asset: string;
          lessons_completed: number;
          students_assigned: number;
          is_pre_assessment: boolean;
          is_post_assessment: boolean;
          all_lessons_count: number;
          lesson_executions: {
            user_id: string;
            execution_url: string | null;
            student_url: string;
            execution_date_iso: string;
            completion_date: string | null;
            student_name: string;
            execution_length: string;
            is_complete: boolean;
            is_good: boolean;
            is_bad: boolean;
            first_suggested_lesson_name: string | null;
            proficiency: number | null;
            proficiency_pre: number | null;
            completed_before: number;
          }[];
          // Include all teacher's students, including those that didn't complete this lesson
          user_info: {
            user_id: string;
            url: string;
            first_name: string;
            last_name: string | null;
            lessons_completed: number;
          }[];
        }
      | {
          is_at_lesson_level: false;
          is_at_teacher_level: true;
          is_at_school_level: false;
          is_at_single_user_level: false;
          school_link: string;
          teacher_id: number;
          teacher_link: string;
          teacher_name: string;
          show_all_lesson_executions: boolean;
          lesson_executions_filtered_the_other_way_url: string;
          lesson_executions: {
            user_id: string;
            execution_url: string | null;
            student_url: string;
            execution_date_iso: string;
            student_name: string;
            lesson_title: string;
            execution_length: string;
            is_complete: boolean;
            is_good: boolean;
            is_bad: boolean;
          }[];
          top_reading_completions: [{ title: string }, string, number][];
          top_math_completions: [{ title: string }, string, number][];
          top_other_completions: [{ title: string }, string, number][];
          top_units: [{ title: string }, string, number][];
          user_info: {
            user_id: string;
            url: string;
            first_name: string;
            last_name: string | null;
            last_week_tutoring_seconds: number;
            this_week_tutoring_seconds: number;
            total_tutoring_seconds: number;
            total_lessons_completed: number;
          }[];
          assessments: {
            lesson_title: string;
            lesson_link: string;
            lessons_completed: number;
            // TODO: change to _iso and format in the client
            last_execution_activity_date: string;
          }[];
          assignments: {
            lesson_title: string;
            lesson_link: string;
            completions: string;
            last_execution_activity_date_iso: string;
          }[];
        }
      | {
          is_at_lesson_level: false;
          is_at_teacher_level: false;
          is_at_school_level: true;
          is_at_single_user_level: false;
          data_download_url?: string;
          top_reading_completions: [{ title: string }, string, number][];
          top_math_completions: [{ title: string }, string, number][];
          top_other_completions: [{ title: string }, string, number][];
          top_units: [{ title: string }, string, number][];
          show_all_lesson_executions?: boolean;
          lesson_executions_filtered_the_other_way_url?: string;
          lesson_executions?: {
            user_id: string;
            execution_url: string | null;
            execution_date_iso: string;
            student_name: string;
            lesson_title: string;
            execution_length: string;
            is_complete: boolean;
            is_good: boolean;
            is_bad: boolean;
          }[];
          teacher_sections: {
            teacher_url: string | null;
            teacher__first_name: string;
            teacher__last_name: string;
            total_count: number;
            lessons_completed: number;
          }[];
        }
      | {
          is_at_lesson_level: false;
          is_at_teacher_level: false;
          is_at_school_level: false;
          is_at_single_user_level: true;
          school_link: string;
          teacher_id: number;
          teacher_link: string;
          teacher_name: string | null;
          student_user_id: number;
          single_user_name: string;
          show_all_lesson_executions: boolean;
          lesson_executions_filtered_the_other_way_url: string;
          lesson_executions: {
            user_id: string;
            execution_url: string | null;
            student_url: string;
            execution_date_iso: string;
            student_name: string;
            lesson_title: string;
            execution_length: string;
            is_complete: boolean;
            is_good: boolean;
            is_bad: boolean;
          }[];
        }
    ))
  | {
      is_at_school_group_level: true;
      is_at_district_level: false;
      is_at_lesson_execution_level: false;
      is_at_lesson_level: false;
      is_at_teacher_level: false;
      is_at_school_level: false;
      is_at_single_user_level: false;
      is_district_link_visible: false;
      secondary_tab_name: string;
      school_group_id: number;
      school_group_name: string;
      student_profile_cnt: number;
      time_spent_hours: number;
      tutoring_value_dollars: number;
      lessons_completed_cnt: number;
      lesson_executions: {
        user_id: string;
        execution_url: string | null;
        student_url: string;
        execution_date_iso: string;
        student_name: string;
        lesson_title: string;
        execution_length: string;
        is_complete: boolean;
        is_good: boolean;
        is_bad: boolean;
      }[];
      top_reading_completions: [{ title: string }, string, number][];
      top_math_completions: [{ title: string }, string, number][];
      top_other_completions: [{ title: string }, string, number][];
      top_units: [{ title: string }, string, number][];
      schools: {
        school_name: string;
        school_link: string;
        student_profile_cnt: number;
        time_spent_hours: number;
        tutoring_value_dollars: number;
        lessons_completed_cnt: number;
      }[];
      user_info: {
        url: string;
        first_name: string;
        last_name: string | null;
        this_week_tutoring_seconds?: number;
        last_week_tutoring_seconds?: number;
        total_tutoring_seconds: number;
        total_lessons_completed: number;
      }[];
    };

export interface SchoolDashboardError {
  error: "sync_pending" | "unsupported_account";
}

export const enum CantPlayAction {
  GoBackToLessonDetails = 0,
  GoBackBeforeLessonDetails = 1,
}

export interface CantPlay {
  title: string;
  description: string;
  image_url: string | null;
  action: CantPlayAction;
}

export interface StripeCheckoutSession {
  publishable_api_key: string;
  checkout_session_id: string;
  client_secret: string;
}

export type ReconcileStripeCheckoutSessionResult =
  | { result: "error_retry_reconciliation"; error_message: string }
  | { result: "error_cannot_retry"; error_message: string }
  | { result: "expired" }
  | {
      result: "pending_checkout";
      publishable_api_key: string;
      client_secret: string;
    }
  | { result: "pending_payment" }
  | { result: "upgraded"; user: User; product_id: number };

export interface FeedbackRequest {
  profile: number | null;
  device_model: string;
  device_os_version: string;
  app_version: string;
  app_build_type: string;
  user_feedback: string;
  device_logs: string;
}
